import React from 'react';
import Popup from './';

export type PopupProps = {
  onClose?: () => void;
  onCancel: () => void;
};

type State = {
  shown: boolean;
  popupType: string;
  onClose: () => void;
};

type Action = {
  action: 'open' | 'close';
  popupType?: string;
  onClose?: () => void;
};

type Dispatch = (action: Action) => void;

const PopupDispatchContext = React.createContext<Dispatch | undefined>(
  undefined
);

const initState: State = {
  shown: false,
  popupType: '',
  onClose: () => {},
};

function PopupReducer(state: State, action: Action) {
  var stateUpdates = {};
  switch (action.action) {
    case 'open':
      if (!action.popupType) {
        console.warn("'popupType' must be given when opening a popup", action);
        return initState;
      }
      stateUpdates = {
        shown: true,
        popupType: action.popupType,
        onClose: action?.onClose,
      };
      break;
    case 'close':
      stateUpdates = {
        shown: false,
      };
      break;
  }

  var newState: State = { ...initState, ...stateUpdates };
  return newState;
}

type Popup = {
  component: React.ComponentType<PopupProps>;
  name: string;
};

type Popups = {
  [key: string]: Popup;
};

function PopupProvider({
  children,
  popups,
}: {
  children: React.ReactNode;
  popups?: Popups;
}) {
  const [state, dispatch] = React.useReducer(PopupReducer, initState);

  let popupComponent = null;
  if (state.shown && popups) {
    const Comp = popups[state.popupType].component;
    popupComponent = (
      <Comp
        onClose={() => {
          if (state.onClose) state.onClose();
          dispatch({ action: 'close' });
        }}
        onCancel={() => {
          dispatch({ action: 'close' });
        }}
      />
    );
  }

  return (
    <PopupDispatchContext.Provider value={dispatch}>
      {popupComponent}
      {children}
    </PopupDispatchContext.Provider>
  );
}

function usePopupDispatch() {
  const context = React.useContext(PopupDispatchContext);
  if (context === undefined) {
    throw new Error('PopupDispatch must be used with PopupProvider');
  }
  return context;
}

export { PopupProvider, usePopupDispatch };
